import {
  AthleteDraftWorkoutDomain,
  BlockDraftWorkoutDomain,
  WorkoutLayerDomain,
} from '@strength/domain';
import { v4 } from 'uuid';
import type { MArgs } from '@strength/loco-sync';
import { parseEmptySlotCellId } from '../utils';
import type { WorkoutWithCycleAndSession } from '../types';
import type { WorkoutLayerCommand } from './types';

/**
 * SC-5512: a create command's identity workoutId is either an empty-slot
 * cell key (a `(cycle, session)` pair, not a workout id) or an All-view add
 * stub's in-memory id. The outgoing day document mints a fresh identity at
 * insert and carries its slot explicitly — a slot looked up by the create's
 * own fresh id cannot be found and previously fell back to slot 0.
 *
 * Exported for the direct-bulkMutate create paths (date edits, the swap
 * drawer's apply-to-other-athletes) so every create resolves its slot the
 * same way.
 */
export function resolveCreateSlot(
  workoutId: string,
  workouts: WorkoutWithCycleAndSession[],
): {
  cycleSortOrder: number;
  sessionSortOrder: number;
  match: WorkoutWithCycleAndSession | undefined;
} {
  // The empty-slot key encodes the cell's slot — use it first so a create
  // replayed after re-renders (deferred template applies) keeps the slot it
  // was planned for.
  const slot = parseEmptySlotCellId(workoutId);
  const match = workouts.find((w) => w.workoutId === workoutId);
  const cycleSortOrder = slot?.cycleSortOrder ?? match?.cycle.id;
  const sessionSortOrder = slot?.sessionSortOrder ?? match?.session.id;
  if (!cycleSortOrder || !sessionSortOrder) {
    // Never send a create without its slot — the server rejects it too, but
    // the old `?? 0` fallback silently landed day documents at slot 0.
    throw new Error(
      'Cannot create a workout layer without a cycle and session slot',
    );
  }
  return { cycleSortOrder, sessionSortOrder, match };
}

/**
 * Block-sheet command runner (moved verbatim from block-sheet.tsx).
 */
export function runBlockMutations(
  commands: WorkoutLayerCommand[],
  blockId: string,
  workouts: WorkoutWithCycleAndSession[],
  bulkMutate: (commands: readonly BlockDraftWorkoutDomain.Command[]) => void,
  getAssignedDate: (workout: WorkoutWithCycleAndSession) => string | null,
) {
  const blockDraftWorkoutCommands: BlockDraftWorkoutDomain.Command[] = [];
  for (const command of commands) {
    // BlockDraftWorkoutDomain.Edit spiritually is a subclass of WorkoutLayerDomain.HasWorkoutLayerEdit, which makes this a safe cast
    const edits = command.edits as BlockDraftWorkoutDomain.Edit[];
    const { exists, workoutId, id } = command.identity;

    let outgoingWorkoutId = workoutId;
    if (!exists) {
      const { cycleSortOrder, sessionSortOrder, match } = resolveCreateSlot(
        workoutId,
        workouts,
      );
      edits.unshift({
        modelName: 'BlockDraftWorkoutLayer',
        modelId: id,
        action: 'create',
        data: {
          assignedDate: match ? getAssignedDate(match) : null,
          cycleSortOrder,
          sessionSortOrder,
          layer: WorkoutLayerDomain.defaultLayer(),
          name: match?.name ?? null,
        },
      });
      // SC-5512: identity is minted at insert — but only for genuinely new
      // day documents. In the block sheet a `!exists` command's matched cell
      // is never a live workout (a live cell always has its block layer), so
      // this mint can only fire for empty-slot cell keys and All-view stubs.
      outgoingWorkoutId = match?.exists ? workoutId : v4();
    }

    blockDraftWorkoutCommands.push({
      module: BlockDraftWorkoutDomain.Module,
      identity: { workoutId: outgoingWorkoutId, blockId },
      edits,
    });
  }

  bulkMutate(blockDraftWorkoutCommands);
}

/**
 * Athlete-sheet command runner (moved verbatim from athlete-sheet.tsx).
 *
 * SC-5512: same minting and explicit-slot rules as `runBlockMutations` —
 * and the linked block layer created alongside an athlete-only day document
 * shares the same minted identity, so both rows stay linked the way
 * id-sharing rows are elsewhere in the grid.
 */
export function runAthleteMutations(
  commands: WorkoutLayerCommand[],
  blockId: string,
  profileId: string,
  workouts: WorkoutWithCycleAndSession[],
  rawMutate: (args: MArgs) => void,
  bulkMutate: (commands: readonly AthleteDraftWorkoutDomain.Command[]) => void,
  // When the All-view add stub is persisted via its first edit, the generic
  // create path doesn't carry a date; this seeds the new athlete layer with the
  // clicked day so the workout lands on the right calendar cell.
  addStubDate?: { workoutId: string; assignedDate: string },
) {
  const blockDraftWorkoutCommands: BlockDraftWorkoutDomain.Command[] = [];
  const athleteDraftWorkoutCommands: AthleteDraftWorkoutDomain.Command[] = [];

  for (const command of commands) {
    // AthleteDraftWorkoutDomain.Edit spiritually is a subclass of WorkoutLayerDomain.HasWorkoutLayerEdit, which makes this a safe cast
    const edits = command.edits as AthleteDraftWorkoutDomain.Edit[];
    const { exists, workoutId, id } = command.identity;

    let outgoingWorkoutId = workoutId;
    if (!exists) {
      const { cycleSortOrder, sessionSortOrder, match } = resolveCreateSlot(
        workoutId,
        workouts,
      );
      // SC-5512: identity is minted at insert — BUT only for genuinely new
      // day documents (empty-slot cell keys, All-view stubs, athlete-only
      // adds). `exists: false` ALSO means 'block-backed cell with no athlete
      // layer yet': that create MUST reuse the block workout's id, or the
      // athlete row inserts unlinked and renders as a duplicate day instead
      // of an overlay (mergeWorkoutLayersByWorkoutId groups by workoutId).
      outgoingWorkoutId = match?.exists ? workoutId : v4();
      const stubAssignedDate =
        addStubDate && addStubDate.workoutId === workoutId
          ? { assignedDate: addStubDate.assignedDate }
          : {};
      if (!match?.exists && !match?.isAthleteOnlyLayer) {
        blockDraftWorkoutCommands.push({
          module: BlockDraftWorkoutDomain.Module,
          identity: { workoutId: outgoingWorkoutId, blockId },
          edits: [
            {
              modelName: 'BlockDraftWorkoutLayer',
              modelId: v4(),
              action: 'create',
              data: {
                cycleSortOrder,
                sessionSortOrder,
                layer: WorkoutLayerDomain.defaultLayer(),
                name: match?.name ?? null,
              },
            },
          ],
        });
      }

      edits.unshift({
        modelName: 'AthleteDraftWorkoutLayer',
        modelId: id,
        action: 'create',
        data: {
          cycleSortOrder,
          sessionSortOrder,
          layer: WorkoutLayerDomain.defaultLayer(),
          name: match?.name ?? null,
          ...stubAssignedDate,
        },
      });
    }

    athleteDraftWorkoutCommands.push({
      module: AthleteDraftWorkoutDomain.Module,
      identity: { workoutId: outgoingWorkoutId, blockId, profileId },
      edits,
    });
  }

  // Run the blockDraftWorkoutCommands first, with the raw loco-sync mutate function, instead of the undo/redo one
  // that is tied to the AthleteDraftWorkoutDomain
  // These edits have no visible effects, so they don't need to be undoable
  for (const command of blockDraftWorkoutCommands) {
    rawMutate(command);
  }

  bulkMutate(athleteDraftWorkoutCommands);
}
